// Record a payment (F-07), shared by Receivables and a customer's Money tab: one payment against
// the invoice it was opened from and, when the customer owes on more than one, across them. That one
// first (opened from it), then the rest oldest due first (spreadPayment), each amount editable, with
// what is applied shown against what was received. Whatever isn't applied stays an unapplied credit
// for a person to apply later (Q29): never applied for them, and asked before it's kept (QA-716). The
// day it came in is today unless the office dates it earlier (QA-715): a cheque that came in Friday.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { CardBeingChecked } from '../history/MoneyActions.tsx';
import type { CardCheck } from '../history/history-model.ts';
import { newId } from '../shared/ids.ts';
import {
  dayText,
  keepCreditAsk,
  money,
  parseMoney,
  receivedOnProblem,
  spreadPayment,
  todayIn,
} from './model.ts';
import type { ArAging } from './types.ts';
import { Loading, Money, useAct, useQuery } from './ui.tsx';

/** What was typed so far: carried to the kept customer when this one is merged meanwhile (QA-559). */
export interface PaymentDraft {
  method: 'cash' | 'cheque' | 'etransfer' | 'card' | 'other' | 'financing';
  amount: string;
  reference: string;
  edits: Record<string, string>;
  /** The day it came in, when the office dated it earlier than today (QA-715). */
  receivedOn?: string;
}

/** One of the customer's open invoices. */
export type OpenInvoice = Pick<ArAging['rows'][number], 'invoiceId' | 'number' | 'dueOn' | 'balanceCents'>;

export interface RecordPaymentProps {
  customerId: string;
  customerName: string;
  /** Opened from this invoice: it takes the amount first. Without it, oldest due first. */
  firstInvoiceId?: string;
  /** The customer's open invoices, when the caller has them; otherwise they're read here. */
  invoices?: readonly OpenInvoice[];
  toast: (m: string) => void;
  /** Saved (true) or closed without saving (false). */
  onDone: (recorded: boolean) => void;
  /** Start from what was typed before (QA-559), instead of the invoice's balance. */
  draft?: PaymentDraft;
  /** Told what is typed, as it changes. */
  onDraft?: (d: PaymentDraft) => void;
  /** The company's time zone: the day it came in starts at its today. */
  timeZone?: string;
  /**
   * The office may date it earlier than today (QA-715): those who edit invoices. Anyone else (a CSR on
   * the phone) records it as it comes in; the server refuses their date.
   */
  dates?: boolean;
}

export function RecordPayment(props: RecordPaymentProps) {
  return props.invoices ? <PaymentForm {...props} list={props.invoices} /> : <WithOpenInvoices {...props} />;
}

/** Reads the customer's open invoices when the caller hasn't them (the Money tab). */
function WithOpenInvoices(props: RecordPaymentProps) {
  const q = useQuery<ArAging>('receivables.aging', { customerId: props.customerId }, [props.customerId]);
  if (!q.data)
    return <Loading error={q.error} what="the customer’s open invoices" retry={() => void q.reload()} />;
  return <PaymentForm {...props} list={q.data.rows} />;
}

function PaymentForm({
  customerId,
  customerName,
  firstInvoiceId,
  list,
  toast,
  onDone,
  draft,
  onDraft,
  timeZone,
  dates = false,
}: RecordPaymentProps & { list: readonly OpenInvoice[] }) {
  const act = useAct(toast, () => undefined);
  const first = list.find((x) => x.invoiceId === firstInvoiceId);
  const [method, setMethod] = useState<PaymentDraft['method']>(draft?.method ?? 'cheque');
  const [amount, setAmount] = useState(
    () =>
      draft?.amount ??
      ((first ? first.balanceCents : list.reduce((t, x) => t + x.balanceCents, 0)) / 100).toFixed(2),
  );
  const [reference, setReference] = useState(draft?.reference ?? '');
  // An amount typed on an invoice, by id; the others follow the spread.
  const [edits, setEdits] = useState<Record<string, string>>(draft?.edits ?? {});
  // today unless dated earlier; only a day the office changed is sent, so today is the server's today
  const today = todayIn(timeZone);
  const [receivedOn, setReceivedOn] = useState((dates && draft?.receivedOn) || today);
  const dated = receivedOn !== today;
  // a payment more than its invoices take: asked once before it's kept as credit (QA-716)
  const [keeping, setKeeping] = useState(false);
  useEffect(() => {
    onDraft?.({ method, amount, reference, edits, ...(dated ? { receivedOn } : {}) });
    setKeeping(false);
  }, [method, amount, reference, edits, receivedOn]);
  // Refused while a card charge on one of the invoices is still being checked (QA-307): that charge,
  // to check again or cancel right here.
  const [held, setHeld] = useState<CardCheck | null>(null);
  const cents = parseMoney(amount);
  const several = list.length > 1;
  // Opened from an invoice: it takes the amount first, then the rest oldest first.
  const spread = spreadPayment(cents ?? 0, list, firstInvoiceId);
  const lines = spread.allocations.map((a) => {
    const inv = list.find((x) => x.invoiceId === a.invoiceId)!;
    const text = edits[a.invoiceId];
    return {
      inv,
      text: text ?? (a.amountCents / 100).toFixed(2),
      cents: text === undefined ? a.amountCents : text.trim() === '' ? 0 : parseMoney(text),
    };
  });
  const applied = lines.reduce((s, l) => s + (l.cents ?? 0), 0);
  const over = lines.find((l) => l.cents !== null && l.cents > l.inv.balanceCents);
  const dayProblem = receivedOnProblem(receivedOn, today);
  const problem =
    !cents || cents <= 0
      ? null
      : dayProblem
        ? dayProblem
        : lines.some((l) => l.cents === null || l.cents < 0)
          ? 'Each amount on an invoice is a number of dollars.'
          : over
            ? `${over.inv.number} owes ${money(over.inv.balanceCents)}; apply no more than that.`
            : applied > cents
              ? `That applies ${money(applied)}, more than the ${money(cents)} received.`
              : method === 'financing' && applied !== cents
                ? 'A financed sale pays its invoices in full: apply the whole amount.'
                : method === 'financing' && !reference.trim()
                  ? 'Give the loan contract number.'
                  : null;
  const credit = (cents ?? 0) - applied;
  const ready = !!cents && cents > 0 && !problem;
  const ask = ready
    ? keepCreditAsk(
        cents,
        applied,
        list.reduce((t, x) => t + Math.max(0, x.balanceCents), 0),
        customerName,
      )
    : null;
  return (
    <form className="fgrid inline-form" aria-label="Record a payment" onSubmit={(e) => e.preventDefault()}>
      <label>
        <span className="flabel">Method</span>
        <select className="field" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
          <option value="cheque">Cheque</option>
          <option value="cash">Cash</option>
          <option value="etransfer">E-transfer</option>
          <option value="financing">Financing (the loan is signed)</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>
        <span className="flabel">Amount</span>
        <input
          className="field"
          inputMode="decimal"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setEdits({}); // a new total spreads afresh
          }}
        />
      </label>
      <label>
        <span className="flabel">{method === 'financing' ? 'Loan contract number' : 'Reference'}</span>
        <input
          className="field"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder={method === 'financing' ? 'From the signed loan contract' : 'Cheque number…'}
        />
      </label>
      {dates && (
        <label>
          <span className="flabel">Received on</span>
          <input
            className="field"
            type="date"
            value={receivedOn}
            max={today}
            onChange={(e) => setReceivedOn(e.target.value)}
          />
        </label>
      )}
      {several && (
        <div className="wide">
          <span className="flabel">Apply to {customerName}’s invoices</span>
          <ul className="rows pay-across">
            {lines.map((l) => (
              <li key={l.inv.invoiceId} className="inv-row">
                <span>
                  <strong>{l.inv.number}</strong>{' '}
                  <span className="muted small">
                    due {dayText(l.inv.dueOn)} · owes <Money cents={l.inv.balanceCents} />
                  </span>
                </span>
                <input
                  className="field num"
                  inputMode="decimal"
                  aria-label={`Amount on ${l.inv.number}`}
                  value={l.text}
                  onChange={(e) => setEdits({ ...edits, [l.inv.invoiceId]: e.target.value })}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      {several && !!cents && cents > 0 && (
        <p className="full small" role="status">
          Applied <strong>{money(applied)}</strong> of {money(cents)} received
          {!problem && credit > 0
            ? `; ${money(credit)} stays an unapplied credit for ${customerName}, to apply later.`
            : '.'}
        </p>
      )}
      {(problem || (!several && ready && credit > 0)) && (
        <p className={`full small${problem ? ' bad-ink' : ' muted'}`} role={problem ? 'alert' : 'note'}>
          {problem ?? `${money(credit)} stays an unapplied credit for ${customerName}, to apply later.`}
        </p>
      )}
      {method === 'financing' && ready && (
        <p className="full small muted" role="note">
          The invoice is paid once the loan contract is signed: the lender owes the money until it pays out,
          which you record under Financing waiting to fund.
        </p>
      )}
      {held && (
        <div className="full">
          <CardBeingChecked
            check={held}
            canAct
            onDone={(m) => {
              setHeld(null);
              toast(m);
            }}
          />
        </div>
      )}
      {keeping && ask && (
        <p className="full small notice" role="alert">
          {ask}
        </p>
      )}
      <div className="row-actions">
        <button
          className="btn primary"
          type="button"
          disabled={act.busy || !ready}
          onClick={() =>
            void (async () => {
              if (ask && !keeping) return setKeeping(true);
              const allocations = lines
                .filter((l) => (l.cents ?? 0) > 0)
                .map((l) => ({ invoiceId: l.inv.invoiceId, amountCents: l.cents! }));
              const ok = await act.run(
                'payments.record',
                {
                  paymentId: newId(),
                  customerId: customerId,
                  method,
                  amountCents: cents,
                  receivedAt: new Date().toISOString(),
                  ...(dated ? { receivedOn } : {}),
                  allocations,
                  ...(reference ? { reference } : {}),
                },
                credit > 0
                  ? `Recorded; ${money(credit)} is an unapplied credit to apply later.`
                  : allocations.length > 1
                    ? `Payment recorded across ${allocations.length} invoices.`
                    : 'Payment recorded.',
                {
                  refused: (e) => {
                    const d =
                      e instanceof RpcError ? (e.detail as { reason?: string; checkoutId?: string }) : null;
                    if (d?.reason === 'card_being_checked' && d.checkoutId)
                      setHeld({ checkoutId: d.checkoutId });
                  },
                },
              );
              if (ok) onDone(true);
            })()
          }
        >
          {keeping && ask ? `Record ${money(cents!)}, keep ${money(credit)} as credit` : 'Record payment'}
        </button>
        {keeping && ask ? (
          <button className="btn quiet" type="button" onClick={() => setKeeping(false)}>
            Change the amount
          </button>
        ) : (
          <button className="btn quiet" type="button" onClick={() => onDone(false)}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
